// RevertInvoiceDialog — confirm returning an approved invoice to the review
// queue. Destructive framing: the approval stamps are cleared, the PO-line
// consumption is freed, and the invoice must be re-approved through the
// normal gate. Optional reason feeds the activity log. Errors from the
// confirm callback render inline; the dialog only closes on success.

import { useState } from "react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Alert from "../../common/Alert";

interface RevertInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
  onConfirm: (reason?: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

const MAX_REASON = 500;

export default function RevertInvoiceDialog({
  isOpen,
  onClose,
  invoiceNumber,
  onConfirm,
  isSubmitting,
}: RevertInvoiceDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: render-time seed guard resets the form on every reopen so a
  // reason typed in a prior session never leaks into the next one.
  const [seededFor, setSeededFor] = useState(false);
  if (isOpen && !seededFor) {
    setSeededFor(true);
    setReason("");
    setError(null);
  } else if (!isOpen && seededFor) {
    setSeededFor(false);
  }

  const handleConfirm = async () => {
    setError(null);
    try {
      const trimmed = reason.trim();
      await onConfirm(trimmed ? trimmed : undefined);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to revert the invoice.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Revert ${invoiceNumber} to review?`}
    >
      <div className="space-y-4">
        <p className="text-body text-slate-600 dark:text-slate-300">
          The invoice returns to the review queue: its approval is removed,
          the PO-line balances it consumed are freed, and it must be edited
          and re-approved through the normal gate.
        </p>
        <div>
          <label htmlFor="revert-invoice-reason" className="label">
            Reason (optional)
          </label>
          <textarea
            id="revert-invoice-reason"
            className="input"
            rows={2}
            maxLength={MAX_REASON}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Revert to review
          </Button>
        </div>
      </div>
    </Modal>
  );
}
